/* eslint-disable no-empty */
import { Injectable } from '@angular/core';
import {
  DebugLoggerConfig,
  DebugLogLevel,
  DebugLogOutputs
} from '@app/models/debug-log.model';
import { environment } from 'src/environments/environment';

import { DebugLogEntry } from './../../models/debug-log.model';

@Injectable({ providedIn: 'root' })
export class DebugLogService {
  private readonly configKey = 'appDebugConfig';
  private readonly enabledAtKey = 'appDebugEnabledAt';
  private readonly logsKey = 'appDebugLogs';
  private readonly sessionLogIdKey = 'appDebugLogId';
  private readonly autoOffMs = 2 * 60 * 60_000;
  private config: DebugLoggerConfig | null = null;
  private buffer: DebugLogEntry[] = [];

  constructor() {
    this.refreshConfig();
    this.attachLifecycleCleanup();
  }

  /**
   * Sets a log config
   * @param config {DebugLoggerConfig}
   */
  setConfig(config: DebugLoggerConfig) {
    const normalized = this.normalizeConfig(config);
    localStorage.setItem(this.configKey, JSON.stringify(normalized));
    this.refreshConfig();
  }

  /**
   * Read, check expiry for config
   */
  refreshConfig(): void {
    this.config = this.readConfig();
    if (!this.config?.enabled) {
      this.clearLogsOnly();
      return;
    }
    this.enforceAutoOff();
    if (!this.config?.enabled) {
      this.clearAll();
      return;
    }
    this.ensureEnabledAt();
    this.ensureLogId();
    this.ensureExpiresAt();
  }

  /**
   * Checks if logging is enabled and if it's within the expiry time.
   * @returns {boolean}
   */
  isEnabled(): boolean {
    if (!this.config?.enabled) return false;

    this.enforceAutoOff();
    if (!this.config?.enabled) return false;

    const expiresAt = this.ensureExpiresAt();
    if (!expiresAt) return false;

    if (Date.now() > expiresAt) {
      this.disableAndClear();
      return false;
    }

    return true;
  }

  /**
   * Log debug level
   * @param args {any[]}
   */
  debug(...args: any[]): void {
    this.write(DebugLogLevel.DEBUG, args);
  }

  /**
   * Log info level
   * @param args {any[]}
   */
  info(...args: any[]): void {
    this.write(DebugLogLevel.INFO, args);
  }

  /**
   * Log warn level
   * @param args {any[]}
   */
  warn(...args: any[]): void {
    this.write(DebugLogLevel.WARN, args);
  }

  /**
   * Log error level
   * @param args {any[]}
   */
  error(...args: any[]): void {
    this.write(DebugLogLevel.ERROR, args);
  }

  /**
   * Set log data captured so far in buffer to localStorage with formatted data.
   * @param args {any[]}
   */
  flush(context?: Record<string, any>): void {
    if (!this.isEnabled()) return;

    const payload = {
      savedAt: new Date().toISOString(),
      logId: this.getLogId(),
      label: this.config?.label || null,
      context: context || null,
      lines: this.buffer.map(e => this.formatLine(e))
    };

    const outputs = this.getOutputs(this.config);
    if (outputs.localStorage) {
      try {
        localStorage.setItem(this.logsKey, JSON.stringify(payload));
      } catch {}
    }
  }

  /**
   * Clears logs from localStorage and empties buffer
   */
  private clearLogsOnly() {
    localStorage.removeItem(this.logsKey);
    localStorage.removeItem(this.enabledAtKey);
    sessionStorage.removeItem(this.sessionLogIdKey);
    this.buffer = [];
  }

  /**
   * Removes all debugging items from localStorage
   */
  private clearAll() {
    this.clearLogsOnly();
    localStorage.removeItem(this.configKey);
  }

  /**
   * Creates log entries and writes to buffer while watching max entries allowed.
   * If log data is not flushed, older logs will be removed while adding new ones.
   * @param level {DebugLogLevel}
   * @param args {any[]}
   * @returns
   */
  private write(level: DebugLogLevel, args: any[]): void {
    if (!this.isEnabled()) return;

    const entry: DebugLogEntry = {
      logId: this.getLogId(),
      timstamp: this.getTimeStamp(),
      iso: new Date().toISOString(),
      level,
      args
    };

    this.buffer.push(entry);

    const max =
      this.config?.maxEntries && this.config.maxEntries > 0
        ? this.config.maxEntries
        : 600;
    if (this.buffer.length > max) {
      this.buffer.splice(0, this.buffer.length - max);
    }

    const outputs = this.getOutputs(this.config);

    if (outputs.console) {
      this.logToConsole(entry);
    }
  }

  /**
   * Logs to console if enabled.
   * @param entry {DebugLogEntry}
   */
  private logToConsole(entry: DebugLogEntry): void {
    const line = this.formatLine(entry);

    switch (entry.level) {
      case DebugLogLevel.DEBUG:
        console.log(line);
        break;
      case DebugLogLevel.INFO:
        console.info(line);
        break;
      case DebugLogLevel.WARN:
        console.warn(line);
        break;
      default:
        console.error(line);
        break;
    }
  }

  /**
   * Given an object, the method formats it to appear as a string line for the logs.
   * @param entry {DebugLogEntry}
   * @returns {string}
   */
  private formatLine(entry: DebugLogEntry): string {
    const parts = entry.args
      .map(a => this.stringifyArg(a))
      .filter(v => v.length > 0);
    const content = parts.length ? parts.join(' ') : '';
    const label = this.config?.label ? `${this.config.label} ` : '';
    return `${entry.timstamp} ${entry.level} ${label}${content}`.trim();
  }

  /**
   * Attempts to stringify any data passed
   * @param arg {any}
   * @returns {string}
   */
  private stringifyArg(arg: any): string {
    try {
      if (arg == null) return '';
      if (typeof arg === 'string') return arg;
      if (typeof arg === 'number' || typeof arg === 'boolean')
        return String(arg);
      return JSON.stringify(arg);
    } catch {
      try {
        return String(arg);
      } catch {
        return '[Unloggable]';
      }
    }
  }

  /**
   * Cleansup or normalizes the config read
   * @param config {DebugLoggerConfig}
   * @returns {DebugLoggerConfig}
   */
  private normalizeConfig(config: DebugLoggerConfig): DebugLoggerConfig {
    return {
      enabled: !!config.enabled,
      outputs: config.outputs ? { ...config.outputs } : undefined,
      durationMs: config.durationMs,
      expiresAtEpochMs: config.expiresAtEpochMs,
      maxEntries: config.maxEntries,
      label:
        typeof config.label === 'string' && config.label.trim()
          ? config.label.trim()
          : undefined
    };
  }

  /**
   * Attempts to read config from localStorage and returns normalized data
   * @returns {DebugLoggerConfig | null}
   */
  private readConfig(): DebugLoggerConfig | null {
    const configStr = localStorage.getItem(this.configKey);
    if (!configStr) return null;

    try {
      return this.normalizeConfig(JSON.parse(configStr) as DebugLoggerConfig);
    } catch {
      return null;
    }
  }

  /**
   * Attempts to read output config from the config object. If not present, logs to LS
   * @param config {DebugLoggerConfig}
   * @returns {DebugLogOutputs}
   */
  private getOutputs(
    config: DebugLoggerConfig | null
  ): Required<DebugLogOutputs> {
    if (!config?.enabled) return { console: false, localStorage: false };

    const hasOutputs =
      !!config.outputs && Object.keys(config.outputs).length > 0;

    if (!hasOutputs) {
      return { console: false, localStorage: true };
    }

    return {
      console: !!config.outputs?.console,
      localStorage: !!config.outputs?.localStorage
    };
  }

  /**
   * Ensures "enabledAt" is there when recording logs
   */
  private ensureEnabledAt(): void {
    const now = Date.now();
    const enabledAtStr = localStorage.getItem(this.enabledAtKey);
    if (enabledAtStr) return;
    try {
      localStorage.setItem(this.enabledAtKey, String(now));
    } catch {}
  }

  /**
   * Checks for an expiry time
   * @returns {number | null}
   */
  private ensureExpiresAt(): number | null {
    if (!this.config?.enabled) return null;

    const existing =
      typeof this.config.expiresAtEpochMs === 'number' &&
      Number.isFinite(this.config.expiresAtEpochMs)
        ? this.config.expiresAtEpochMs
        : null;

    if (existing) return existing;

    const duration =
      typeof this.config.durationMs === 'number' && this.config.durationMs > 0
        ? this.config.durationMs
        : this.getDefaultDurationMs();

    const expiresAtEpochMs = Date.now() + duration;
    const next: DebugLoggerConfig = { ...this.config, expiresAtEpochMs };
    this.config = next;

    try {
      localStorage.setItem(this.configKey, JSON.stringify(next));
    } catch {}

    return expiresAtEpochMs;
  }

  /**
   * Returns default logging duration of 5 mins for PROD and 2 mins for other env.
   * @returns {number}
   */
  private getDefaultDurationMs(): number {
    return environment.production ? 5 * 60_000 : 2 * 60_000;
  }

  /**
   * enforces automatically turning off logging
   * creates timestamp when logging is on. If not off after a set time, turns off in 2 hrs.
   */
  private enforceAutoOff(): void {
    const config = this.readConfig();
    if (!config?.enabled) {
      this.config = config;
      return;
    }

    const now = Date.now();
    const enabledAtRaw = localStorage.getItem(this.enabledAtKey);

    if (!enabledAtRaw) {
      try {
        localStorage.setItem(this.enabledAtKey, String(now));
      } catch {}
      return;
    }

    const enabledAt = Number(enabledAtRaw);
    if (!Number.isFinite(enabledAt)) {
      this.disableAndClear();
      return;
    }

    if (now - enabledAt >= this.autoOffMs) {
      this.disableAndClear();
    }
  }

  /**
   * Disables logging and calls clear logs
   */
  private disableAndClear(): void {
    const config = this.readConfig();
    if (config) {
      const next = { ...config, enabled: false };
      try {
        localStorage.setItem(this.configKey, JSON.stringify(next));
      } catch {}
    }
    this.clearAll();
  }

  /**
   * Ensures a LogId exists for a debug session
   */
  private ensureLogId(): void {
    if (sessionStorage.getItem(this.sessionLogIdKey)) return;
    sessionStorage.setItem(this.sessionLogIdKey, this.createLogId());
  }

  /**
   * Returns a logId
   * @returns {string}
   */
  private getLogId(): string {
    this.ensureLogId();
    return sessionStorage.getItem(this.sessionLogIdKey) || this.createLogId();
  }

  /**
   * Creates an id using date
   * @returns {string}
   */
  private createLogId(): string {
    const d = new Date();
    const y = String(d.getFullYear());
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const da = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    const rnd = Math.random().toString(16).slice(2, 8).toUpperCase();
    return `${y}${mo}${da}-${hh}${mm}${ss}-${rnd}`;
  }

  /**
   * Returns timestamp
   * @returns {string}
   */
  private getTimeStamp(): string {
    const d = new Date();
    const day = String(d.getDate()).padStart(2, '0');
    const month = this.getMonthAsString(d.getMonth());
    const hour = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${day}${month} ${hour}:${min}`;
  }

  /**
   * Given an index, returns month as a 3 character string
   * @param idx {number}
   * @returns {string}
   */
  private getMonthAsString(idx: number): string {
    switch (idx) {
      case 0:
        return 'JAN';
      case 1:
        return 'FEB';
      case 2:
        return 'MAR';
      case 3:
        return 'APR';
      case 4:
        return 'MAY';
      case 5:
        return 'JUN';
      case 6:
        return 'JUL';
      case 7:
        return 'AUG';
      case 8:
        return 'SEP';
      case 9:
        return 'OCT';
      case 10:
        return 'NOV';
      default:
        return 'DEC';
    }
  }

  /**
   * Method to detect Page unload or tab close events and clear logs
   */
  private attachLifecycleCleanup(): void {
    const handler = () => {
      this.clearLogsOnly();
    };

    window.addEventListener('pagehide', handler);
    window.addEventListener('beforeunload', handler);
  }
}

/**
To DEBUG:
1. Open console
2. Inset below script
localStorage.setItem(
  'appDebugConfig',
  JSON.stringify({
    enabled: true,
    label: 'MAX',
    outputs: {
      localStorage: true,
      console: true
    },
    durationMs: 3 * 60 * 60_000,
    maxEntries: 50000
  })
);
location.reload();

Above is max setting. console/localStorage outputs can be disabled.
Don't configure expiresAtEpochMs as it is calculated internally.
*/
